import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import ConfigurationFlowFooter from '@/components/modules/configuration-flow/configuration-flow-footer/configuration-flow-footer';
import ConfigurationFlowHeader from '@/components/modules/configuration-flow/configuration-flow-header/configuration-flow-header';
import ConfigurationFlowSection from '@/components/modules/configuration-flow/configuration-flow-section/configuration-flow-section';
import Button from '@/components/molecules/button/button';
import BookingFlowLoader from '@/components/organisms/booking-flow-loader/booking-flow-loader';
import Container from '@/components/utilities/container/container';
import ConfigurationFlowModuleList, {
  ConfigurationFlowModule,
} from '@/components/utilities/content-modules/configuration-flow-module-list/configuration-flow-module-list';
import Modal, { ModalProps } from '@/components/utilities/modal/modal';
import { useHotelContext } from '@/libraries/contexts/hotel-context/hotel-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { capitalizeFirstLetter } from '@/libraries/formatters/capitalize-first-letter';
import useIsInView from '@/libraries/hooks/use-is-in-view';
import { usePageTransition } from '@/libraries/hooks/use-page-transition';
import styles from './configuration-flow-modal.module.scss';

export interface ConfigurationFlowModalProps
  extends Omit<ModalProps, 'children'> {
  /** The text to show as a headline */
  headline: string;
  /** An event that is triggered on close */
  onClose: () => void;
  /** Parameters from the current page */
  parameters: Content.Components['ConfigurationFlowModuleDto']['parameters'];
  /** The steps of the configuration flow */
  steps: Content.Components['ConfigurationFlowModuleDto']['steps'];
}

const ConfigurationFlowModal = ({
  headline,
  isOpen,
  onClose,
  parameters,
  steps,
  ...rest
}: ConfigurationFlowModalProps) => {
  const { t } = useI18n();
  const footerRef = useRef<HTMLDivElement>(null);
  const [renderStickyFooter, setRenderStickyFooter] = useState(false);
  const [showBookingFlowLoader, setShowBookingFlowLoader] = useState(false);
  const { currentStep, setCurrentStep, setParameters, totalPrice, url } =
    useHotelContext();

  const { position: footerPosition } = useIsInView(footerRef, {
    rootMargin: `0px 0px 0px 0px`,
  });

  useEffect(() => {
    setRenderStickyFooter(
      footerPosition !== null && footerPosition !== 'inView',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footerPosition]);

  useEffect(() => {
    setParameters(parameters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameters]);

  usePageTransition({
    onEnter: () => {
      setShowBookingFlowLoader(false);
    },
  });

  const step = useMemo(() => {
    return steps[currentStep - 1];
  }, [currentStep, steps]);

  const renderButtons = () => (
    <div className={styles.buttons}>
      {currentStep > 2 ? (
        <Button
          className={styles.backButton}
          onClick={() => {
            setCurrentStep(value => value - 1);
          }}
          text={t('items.common.words.back')}
          variant="tertiary"
        />
      ) : (
        <Button
          className={styles.cancelButton}
          onClick={onClose}
          text={t('items.common.words.cancel')}
          variant="tertiary"
        />
      )}

      {currentStep === steps.length ? (
        url ? (
          <Button
            onClick={() => setShowBookingFlowLoader(true)}
            text={
              steps[currentStep - 1]?.nextStepText ??
              t('items.common.words.buyNow')
            }
            url={url}
            variant="primary"
          />
        ) : null
      ) : (
        <Button
          onClick={() => {
            setCurrentStep(value => value + 1);
          }}
          text={
            steps[currentStep - 1]?.nextStepText ??
            capitalizeFirstLetter(t('items.common.words.next'))
          }
          variant="primary"
        />
      )}
    </div>
  );

  const renderConfigurationFlowFooter = () => (
    <ConfigurationFlowFooter
      isFirstStep={currentStep === 1}
      totalPrice={totalPrice}
    >
      {renderButtons()}
    </ConfigurationFlowFooter>
  );

  if (!isOpen) return null;

  return (
    <Modal
      {...rest}
      className={styles.component}
      isOpen={isOpen}
      onClickOutside={onClose}
      setIsOpen={onClose}
    >
      <div className={styles.curtain}>
        <section className={styles.section}>
          <div className={styles.modalActions}>
            <Typography className={styles.text} tagStyle="bodyMedium">
              {t('items.common.words.cancel')}
            </Typography>

            <Button
              aria-label={t('items.common.words.cancel')}
              className={styles.close}
              icon="Close"
              iconSize={16}
              onClick={onClose}
              variant="tertiary"
            />
          </div>

          <Container className={styles.container}>
            <ConfigurationFlowHeader
              currentStep={currentStep}
              headline={headline}
              steps={steps}
            />

            <div className={styles.contentWrapper}>
              {steps.map(
                (step, stepIndex) =>
                  stepIndex < currentStep && (
                    <ConfigurationFlowSection
                      key={stepIndex}
                      currentStep={stepIndex + 1}
                      isActive={stepIndex + 1 === currentStep}
                      isCompleted={stepIndex + 1 < currentStep}
                      step={step}
                    />
                  ),
              )}

              <div className={styles.content}>
                {(step.description || step.headline || step.link) && (
                  <div className={styles.stepTextWrapper}>
                    {step.headline && (
                      <Typography
                        className={styles.stepHeadline}
                        tagStyle="headingMedium"
                      >
                        {step.headline}
                      </Typography>
                    )}

                    {step.description &&
                      (step.link ? (
                        <RichText
                          className={styles.stepDescription}
                          text={`${step.description} <a href="${step.link.url}" target="${step.link.target}">${step.link.text}</a>`}
                        />
                      ) : (
                        <RichText
                          className={styles.stepDescription}
                          text={step.description}
                        />
                      ))}
                  </div>
                )}

                {step && (
                  <ConfigurationFlowModuleList
                    contentModules={
                      step.contentModules as unknown as ConfigurationFlowModule[]
                    }
                  />
                )}

                <div ref={footerRef}>{renderConfigurationFlowFooter()}</div>
              </div>
            </div>
          </Container>

          <Container
            className={classNames(styles.stickyFooter, {
              [styles.isVisible]: renderStickyFooter,
            })}
          >
            {renderConfigurationFlowFooter()}
          </Container>
        </section>
      </div>

      <BookingFlowLoader
        isOpen={showBookingFlowLoader}
        onClose={() => setShowBookingFlowLoader(false)}
      />
    </Modal>
  );
};

ConfigurationFlowModal.displayName = 'ConfigurationFlowModal';

export default ConfigurationFlowModal;
